import { Form } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { markShippedRedemptionPath } from "@/routes"

/**
 * OKS-563 — Mark-shipped dialog for a pending Redemption row in the
 * brand-side queue (UX Spec §2.2).
 *
 * # Why `Dialog` and not `AlertDialog` (UX-OQ-1)
 *
 * Its two siblings (`fulfill-redemption-dialog`, `cancel-redemption-dialog`)
 * use `AlertDialog`, the irreversible-action primitive. This one deliberately
 * does not: it TAKES INPUT, and it is not destructive. The resulting visual
 * asymmetry is correct signalling rather than an inconsistency — an
 * AlertDialog that contains a text field tells the user "this is a
 * point of no return" about an optional, additive step.
 *
 * # Form rules (Crew hard rule)
 *
 * Inertia `<Form>` + plain `Input`/`Label` with `name` attributes; errors from
 * the render-prop `errors` object. **NO shadcn `Form`/`FormField`/`FormItem`/
 * `FormMessage`, NO react-hook-form, NO zod.** Rails validations flow back
 * through Inertia.
 *
 * # Two error channels, deliberately not one
 *
 * - `errors.tracking_number` → renders UNDER the Input. This is the
 *   80-char cap: a field-level problem the admin can fix in place.
 * - `errors.base` → renders as a destructive `Alert` above the footer. This
 *   is the already-actioned race loss: nothing about the field is wrong, and
 *   putting it under the Input would tell the admin to edit a value that is
 *   not the problem.
 *
 * The server picks the channel (`RedemptionsController#mark_shipped` branches
 * on `:tracking_number_too_long`); this component just renders whichever
 * arrives.
 *
 * # Failure keeps the dialog open
 *
 * `onSuccess` closes it. A failure response leaves it mounted with the
 * inline error, so the admin does not lose what they typed (UX Spec §4 —
 * never a toast-only failure).
 */
interface MarkShippedDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  redemptionId: number | null
  creatorName: string
  rewardTitle: string
}

export function MarkShippedDialog({
  open,
  onOpenChange,
  redemptionId,
  creatorName,
  rewardTitle,
}: MarkShippedDialogProps) {
  const { t } = useTranslation()

  if (redemptionId === null) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mark-shipped-dialog">
        <Form
          action={markShippedRedemptionPath(redemptionId)}
          method="patch"
          options={{ preserveScroll: true }}
          onSuccess={() => {
            onOpenChange(false)
          }}
        >
          {({ processing, errors }) => (
            <>
              <DialogHeader>
                <DialogTitle data-testid="mark-shipped-dialog-title">
                  {t("rewards.redemptions.mark_shipped_dialog.title")}
                </DialogTitle>
                <DialogDescription data-testid="mark-shipped-dialog-description">
                  {t("rewards.redemptions.mark_shipped_dialog.body", {
                    creator: creatorName,
                    reward: rewardTitle,
                  })}
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-2 py-2">
                <Label htmlFor="tracking_number">
                  {t("rewards.redemptions.mark_shipped_dialog.tracking_label")}
                </Label>
                <Input
                  id="tracking_number"
                  name="tracking_number"
                  type="text"
                  maxLength={80}
                  autoComplete="off"
                  disabled={processing}
                  aria-describedby="tracking_number_helper"
                  aria-invalid={errors.tracking_number ? true : undefined}
                  placeholder={t(
                    "rewards.redemptions.mark_shipped_dialog.tracking_placeholder",
                  )}
                  data-testid="mark-shipped-dialog-tracking-input"
                />
                {errors.tracking_number ? (
                  <p
                    role="alert"
                    className="text-destructive text-sm"
                    data-testid="mark-shipped-dialog-tracking-error"
                  >
                    {errors.tracking_number}
                  </p>
                ) : (
                  <p
                    id="tracking_number_helper"
                    className="text-muted-foreground text-sm"
                    data-testid="mark-shipped-dialog-tracking-helper"
                  >
                    {t(
                      "rewards.redemptions.mark_shipped_dialog.tracking_helper",
                    )}
                  </p>
                )}
              </div>

              {errors.base ? (
                <Alert
                  variant="destructive"
                  role="alert"
                  data-testid="mark-shipped-dialog-error"
                  className="mt-2"
                >
                  <AlertDescription>{errors.base}</AlertDescription>
                </Alert>
              ) : null}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={processing}
                  onClick={() => onOpenChange(false)}
                  data-testid="mark-shipped-dialog-cancel"
                >
                  {t("rewards.redemptions.mark_shipped_dialog.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={processing}
                  aria-busy={processing}
                  data-testid="mark-shipped-dialog-confirm"
                >
                  {processing ? (
                    <Loader2
                      aria-hidden="true"
                      className="mr-2 size-4 animate-spin"
                    />
                  ) : null}
                  {t("rewards.redemptions.mark_shipped_dialog.confirm")}
                </Button>
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
